import { useState } from "react"
import Dialog, { type DialogProps } from "@/v2-ui/dialog"
import Button from "@/v2-backoffice-client/components/button"
import TenantActivationStatusBadge from "@/v2-backoffice-client/tenant/migration/TenantActivationStatusBadge"
import MigrationCenterDialog from "@/v2-backoffice-client/tenant/migration/center/MigrationCenterDialog"
import { formatUserName } from "@/v2-backoffice-client/user/user.utils"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"

export type UserMigratePromptAction = "invite" | "create"

export type UserMigratePromptDialogProps = DialogProps & {
  /** The existing, not-yet-activated users found among the batch's emails. */
  users: UserFragment[]
  /** What the operator was doing — words the explanation and the buttons. */
  action: UserMigratePromptAction
  /** How many rows the batch keeps once these users are taken out of it. */
  remainingCount: number
  /**
   * Go ahead with the original batch, these users included. Invitations
   * offer it (an existing user CAN redeem one by signing in); creates don't
   * — the API refuses a second account for a claimed email regardless.
   */
  onProceedAnyway?(): void
  /**
   * The operator chose activation: the host takes these users out of its
   * batch and sends the rest. The Migration Center then opens on them.
   */
  onSendActivation(): void
}

const COPY: Record<
  UserMigratePromptAction,
  {
    /** Why the operator's action is wrong for an existing account. */
    why: string
    /** The proceed-regardless button; only actions that offer one have it. */
    proceedLabel?: string
    /** What happens to the rest of the batch and to these people. */
    consequence(several: boolean, remainingCount: number): string
  }
> = {
  invite: {
    why: "An invitation creates a new account, so their link would end on “a user with this ID already exists”.",
    proceedLabel: "Invite anyway",
    consequence(several, remainingCount) {
      const rest =
        remainingCount > 0
          ? `The other ${remainingCount} invitation${remainingCount === 1 ? "" : "s"} go out as usual. `
          : ""
      return `${rest}${several ? "These people" : "This person"} won't be invited — to add them to a team, use Add member on the team.`
    },
  },
  create: {
    why: "A second account for the same email is refused.",
    consequence(several, remainingCount) {
      const rest =
        remainingCount > 0
          ? `The other ${remainingCount} user${remainingCount === 1 ? "" : "s"} are created as usual. `
          : ""
      return `${rest}${several ? "These people" : "This person"} won't be created.`
    },
  },
}

/**
 * The guard the invite and create dialogs raise before they send (FUS-927,
 * the 2026-10-28 support case): some of the addresses belong to people who
 * already have a Placepoint account but have not activated Fusion login. For
 * them the right tool is the login migration — a personal activation link
 * for the account they already have — not an invitation or a fresh
 * account, which the CS operator cannot tell apart from here otherwise.
 *
 * "Send activation instead" hands the users to the Migration Center (THE
 * migration surface: live status per member, per-member and bulk sends,
 * multi-team handling) and lets the host finish the rest of its batch.
 */
function UserMigratePromptDialog(props: UserMigratePromptDialogProps) {
  const {
    users,
    action,
    remainingCount,
    onProceedAnyway,
    onSendActivation,
    open,
    onOpenChange,
    ...dialogProps
  } = props

  // The center's own copy of the selection: the host dismisses the prompt
  // (and may empty `users`) the moment the operator picks activation, while
  // the center has to stay up with exactly the people they picked it for.
  const [centerUsers, setCenterUsers] = useState<UserFragment[] | null>(null)
  const copy = COPY[action]
  const several = users.length > 1
  // Activations are tenant-scoped: a user with no team has nothing the
  // center can send under, so with nobody to send to the button is off
  // (each such row says why) instead of opening an empty center.
  const canSendActivation = users.some((user) => user.Tenants.length > 0)

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange} {...dialogProps}>
        <Dialog.Content align="top" className="max-w-xl">
          <Dialog.Header>
            <Dialog.Title>
              {several
                ? `${users.length} people already have accounts`
                : "This person already has an account"}
            </Dialog.Title>
            <Dialog.Description>
              They already exist in Placepoint but haven&apos;t activated Fusion
              login. {copy.why} Send them a Fusion login activation instead: a
              personal link (valid 30 days) to set a password on the account
              they already have — nobody is locked out meanwhile.
            </Dialog.Description>
          </Dialog.Header>

          <ul className="my-4 max-h-72 divide-y divide-border overflow-y-auto rounded-md border border-border text-sm">
            {users.map((user) => (
              <li
                key={user.Id}
                className="flex items-center justify-between gap-3 px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium">
                    {formatUserName(user.FirstName, user.LastName)}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {user.Email}
                    {user.CustomerNames.length > 0 &&
                      ` · ${user.CustomerNames.join(", ")}`}
                  </p>
                  {user.Tenants.length === 0 && (
                    <p className="text-xs text-amber-500">
                      No team — add them to a team before sending an activation.
                    </p>
                  )}
                </div>
                <TenantActivationStatusBadge
                  className="shrink-0"
                  status={user.MigrationStatus}
                  experience={user.ActivationExperience}
                />
              </li>
            ))}
          </ul>

          <p className="text-xs text-muted-foreground">
            {copy.consequence(several, remainingCount)} Fusion login migration
            opens next, where you send the activation links.
          </p>

          <Dialog.Footer className="mt-4 flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange?.(false)
              }}
            >
              Back
            </Button>
            {onProceedAnyway && copy.proceedLabel && (
              <Button type="button" variant="outline" onClick={onProceedAnyway}>
                {copy.proceedLabel}
              </Button>
            )}
            <Button
              type="button"
              iconSrc="key"
              disabled={!canSendActivation}
              onClick={() => {
                setCenterUsers(users)
                onSendActivation()
              }}
            >
              Send activation instead
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>

      {/* A sibling, not a child, of the prompt: it stays up after the prompt
          (and the host dialog) close. */}
      <MigrationCenterDialog
        open={centerUsers !== null}
        onOpenChange={(next) => {
          if (!next) setCenterUsers(null)
        }}
        selection={{ kind: "users", users: centerUsers ?? [] }}
      />
    </>
  )
}

export default UserMigratePromptDialog
